import { type Company, type RealEstate } from '@pigello/pigello-matrix';
import { readFromLocalStorage } from '../local-storage/local-storage';
import { getSessionValues } from '@/providers/session-provider/get-session-values';
import { tryCatch } from '@/utils/try-catch';
import { ensureGetList } from '@/requests/hooks/use-get-list';
import { ObjectKeys } from '../utils';
import {
  GLOBAL_FILTER_DEFAULT_VALUES,
  GLOBAL_FILTER_KEY,
  QUERYABLE_FILTER_KEYS,
} from './constants';
import type { GlobalFilter } from './types';

const PAGE_PARAMS = {
  page: 1,
  pageSize: 100,
} as const;

/**
 * Fetches real estate IDs from external clients by first getting their companies,
 * then getting real estates owned by those companies
 */
async function fetchRealEstatesFromExternalClients(
  externalClientIds: string[]
): Promise<string[]> {
  const { data: companyIds, error: companyError } = await tryCatch(
    async () =>
      await ensureGetList<Company>({
        modelName: 'company',
        queryParams: {
          ...PAGE_PARAMS,
          slim: true,
          filters: {
            managed_by: {
              __in: externalClientIds.join(','),
            },
          },
        },
      })
  );

  if (!companyIds?.list?.length || companyError) {
    return [];
  }

  const { data: realEstateIds, error: realestateCompanyError } = await tryCatch(
    async () => {
      const realestates = await ensureGetList<RealEstate>({
        modelName: 'realestate',
        queryParams: {
          ...PAGE_PARAMS,
          slim: true,
          filters: {
            owned_by: {
              __in: companyIds?.list?.map((c) => c.id).join(','),
            },
          },
        },
      });
      return realestates?.list.map((r) => r.id);
    }
  );

  if (!realEstateIds?.length || realestateCompanyError) {
    return [];
  }

  return realEstateIds;
}

/**
 * Fetches real estate IDs directly from company IDs
 */
async function fetchRealEstatesFromCompanies(
  companyIds: string[]
): Promise<string[]> {
  const { data: realEstateIds, error: realestateError } = await tryCatch(
    async () => {
      const realestates = await ensureGetList<RealEstate>({
        modelName: 'realestate',
        queryParams: {
          ...PAGE_PARAMS,
          slim: true,
          filters: {
            owned_by: {
              __in: companyIds.join(','),
            },
          },
        },
      });
      return realestates?.list.map((r) => r.id);
    }
  );

  if (!realEstateIds?.length || realestateError) {
    return [];
  }

  return realEstateIds;
}

export const getGlobalFilterQuery = async (
  enableGlobalFilters?: boolean
): Promise<string> => {
  const { organizationId } = getSessionValues();

  let query = `&organization=${organizationId}`;

  if (!enableGlobalFilters) {
    return query;
  }

  const globalFilter = readFromLocalStorage<GlobalFilter>({
    key: GLOBAL_FILTER_KEY,
    version: 1,
    defaultValue: {
      [organizationId]: GLOBAL_FILTER_DEFAULT_VALUES,
    },
  });

  const currentFiltering = { ...globalFilter[organizationId] };
  const externalClientIds = currentFiltering.filter_external_clients || [];
  const globalCompanyIds = currentFiltering.global_companies || [];

  // Prepare concurrent lookups
  const lookupPromises: Array<Promise<string[]>> = [];

  if (externalClientIds.length > 0) {
    lookupPromises.push(fetchRealEstatesFromExternalClients(externalClientIds));
  }

  if (globalCompanyIds.length > 0) {
    lookupPromises.push(fetchRealEstatesFromCompanies(globalCompanyIds));
  }

  // Wait for all lookups to complete and merge results
  if (lookupPromises.length > 0) {
    const results = await Promise.all(lookupPromises);

    const allRealEstateIds = results.flatMap((result) => result);

    if (allRealEstateIds.length > 0) {
      const uniqueRealEstateIds = new Set([
        ...(currentFiltering.global_realestates ?? []),
        ...allRealEstateIds,
      ]);
      currentFiltering.global_realestates = Array.from(uniqueRealEstateIds);
    }
  }

  for (const key of ObjectKeys(currentFiltering)) {
    if (currentFiltering[key].length > 0 && QUERYABLE_FILTER_KEYS.has(key)) {
      query += `&${key}=${currentFiltering[key].join(',')}`;
    }
  }

  return query;
};
